import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { translateLocation } from '../../utils/translationUtils';
import Modal from '../../components/Modal';
import './Cohorts.css';

const Cohorts = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedLocation } = useOutletContext() || {};
  const { data, addCohort, updateCohort, deleteCohort } = useData();
  const managerLocation = (user?.location || '').toLowerCase();

  const createEmptyForm = () => ({
    name: '',
    code: '',
    description: '',
    location: user?.role === 'manager'
      ? managerLocation
      : (selectedLocation && selectedLocation !== 'all' ? selectedLocation : 'paramaribo'),
    studentIds: [],
    studyIds: [],
    moduleIds: [],
    isActive: true
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCohort, setEditingCohort] = useState(null);
  const [cohortForm, setCohortForm] = useState(createEmptyForm());
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudentToAdd, setSelectedStudentToAdd] = useState('');
  const [selectedStudentToRemove, setSelectedStudentToRemove] = useState('');
  const [selectedStudyToAdd, setSelectedStudyToAdd] = useState('');
  const [selectedStudyToRemove, setSelectedStudyToRemove] = useState('');

  const cohorts = useMemo(() => {
    return (data.cohorts || []).filter((cohort) => {
      const locationMatches = !selectedLocation || selectedLocation === 'all'
        ? true
        : String(cohort.location || '').toLowerCase() === String(selectedLocation).toLowerCase();
      if (!locationMatches) return false;

      const haystack = [cohort.name, cohort.code, cohort.description, cohort.location]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(searchTerm.trim().toLowerCase());
    });
  }, [data.cohorts, searchTerm, selectedLocation]);

  const visibleStudents = useMemo(() => {
    return (data.students || []).filter((student) => {
      if (user?.role === 'manager') {
        return String(student.location || '').toLowerCase() === managerLocation;
      }
      if (cohortForm.location) {
        return String(student.location || '').toLowerCase() === String(cohortForm.location).toLowerCase();
      }
      return true;
    });
  }, [cohortForm.location, data.students, managerLocation, user?.role]);

  const visibleStudies = useMemo(() => {
    return (data.studies || []).filter((study) => {
      if (user?.role === 'manager') {
        return String(study.location || '').toLowerCase() === managerLocation;
      }
      if (cohortForm.location) {
        return String(study.location || '').toLowerCase() === String(cohortForm.location).toLowerCase();
      }
      return true;
    });
  }, [cohortForm.location, data.studies, managerLocation, user?.role]);

  const studiesWithModules = useMemo(() => {
    return visibleStudies.filter((study) =>
      (study.studyYears || []).some((year) => (year.modules || []).length > 0)
    );
  }, [visibleStudies]);

  const studentsWithoutCohort = useMemo(() => {
    return visibleStudents.filter((student) => !student.cohort?.id && !cohortForm.studentIds.includes(student.id));
  }, [cohortForm.studentIds, visibleStudents]);

  const studentsInCohort = useMemo(() => {
    return visibleStudents.filter((student) => cohortForm.studentIds.includes(student.id));
  }, [cohortForm.studentIds, visibleStudents]);

  const studiesAvailableToAdd = useMemo(() => {
    return studiesWithModules.filter((study) => !cohortForm.studyIds.includes(study.id));
  }, [cohortForm.studyIds, studiesWithModules]);

  const studiesInCohort = useMemo(() => {
    return visibleStudies.filter((study) => cohortForm.studyIds.includes(study.id));
  }, [cohortForm.studyIds, visibleStudies]);

  const visibleModules = useMemo(() => {
    const selectedStudyIds = new Set(cohortForm.studyIds || []);
    const sourceStudies = selectedStudyIds.size > 0
      ? studiesWithModules.filter((study) => selectedStudyIds.has(study.id))
      : studiesWithModules;

    const seenModuleIds = new Set();
    const modules = [];
    sourceStudies.forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        (year.modules || []).forEach((moduleDoc) => {
          const moduleId = String(moduleDoc.id || moduleDoc._id || '');
          if (!moduleId || seenModuleIds.has(moduleId)) return;
          seenModuleIds.add(moduleId);
          modules.push({
            id: moduleId,
            name: moduleDoc.name,
            code: moduleDoc.code,
            studyName: study.name,
            studyId: String(study.id || study._id),
            yearId: String(year.id || year._id),
            yearLabel: year.name || `Studiejaar ${year.yearNumber || year.year}`
          });
        });
      });
    });
    return modules;
  }, [cohortForm.studyIds, studiesWithModules]);

  const moduleGroups = useMemo(() => {
    const grouped = new Map();
    visibleModules.forEach((moduleDoc) => {
      const key = `${moduleDoc.studyId}:${moduleDoc.yearId}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          studyName: moduleDoc.studyName,
          yearLabel: moduleDoc.yearLabel,
          modules: []
        });
      }
      grouped.get(key).modules.push(moduleDoc);
    });

    return Array.from(grouped.values()).sort((left, right) => {
      const studyCompare = left.studyName.localeCompare(right.studyName, 'nl');
      if (studyCompare !== 0) return studyCompare;
      return left.yearLabel.localeCompare(right.yearLabel, 'nl');
    });
  }, [visibleModules]);

  const openCreateModal = () => {
    setCohortForm(createEmptyForm());
    setEditingCohort(null);
    setSelectedStudentToAdd('');
    setSelectedStudentToRemove('');
    setSelectedStudyToAdd('');
    setSelectedStudyToRemove('');
    setShowCreateModal(true);
  };

  const openEditModal = (cohort) => {
    setEditingCohort(cohort);
    setCohortForm({
      name: cohort.name || '',
      code: cohort.code || '',
      description: cohort.description || '',
      location: String(cohort.location || '').toLowerCase(),
      studentIds: cohort.studentIds || [],
      studyIds: (cohort.studies || []).map((study) => String(study.id || study._id || study)),
      moduleIds: (cohort.modules || []).map((moduleDoc) => String(moduleDoc.id || moduleDoc._id || moduleDoc)),
      isActive: cohort.isActive !== false
    });
    setSelectedStudentToAdd('');
    setSelectedStudentToRemove('');
    setSelectedStudyToAdd('');
    setSelectedStudyToRemove('');
    setShowCreateModal(true);
  };

  const addStudentToCohort = () => {
    if (!selectedStudentToAdd) return;
    setCohortForm((prev) => ({
      ...prev,
      studentIds: Array.from(new Set([...prev.studentIds, selectedStudentToAdd]))
    }));
    setSelectedStudentToAdd('');
  };

  const removeStudentFromCohort = () => {
    if (!selectedStudentToRemove) return;
    setCohortForm((prev) => ({
      ...prev,
      studentIds: prev.studentIds.filter((id) => id !== selectedStudentToRemove)
    }));
    setSelectedStudentToRemove('');
  };

  const addStudyToCohort = () => {
    if (!selectedStudyToAdd) return;
    setCohortForm((prev) => ({
      ...prev,
      studyIds: Array.from(new Set([...prev.studyIds, selectedStudyToAdd]))
    }));
    setSelectedStudyToAdd('');
  };

  const removeStudyFromCohort = () => {
    if (!selectedStudyToRemove) return;
    setCohortForm((prev) => ({
      ...prev,
      studyIds: prev.studyIds.filter((id) => id !== selectedStudyToRemove)
    }));
    setSelectedStudyToRemove('');
  };

  const toggleModuleSelection = (moduleId) => {
    setCohortForm((prev) => {
      const hasModule = prev.moduleIds.includes(moduleId);
      return {
        ...prev,
        moduleIds: hasModule
          ? prev.moduleIds.filter((id) => id !== moduleId)
          : [...prev.moduleIds, moduleId]
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      ...cohortForm,
      location: String(cohortForm.location || '').toLowerCase()
    };

    try {
      if (editingCohort?.id) {
        await updateCohort(editingCohort.id, payload);
      } else {
        await addCohort(payload);
      }
      setShowCreateModal(false);
      setEditingCohort(null);
      setCohortForm(createEmptyForm());
      setSelectedStudentToAdd('');
      setSelectedStudentToRemove('');
      setSelectedStudyToAdd('');
      setSelectedStudyToRemove('');
    } catch (error) {
      window.alert(error.message || 'Cohort opslaan mislukt');
    }
  };

  const handleDelete = async (cohortId) => {
    if (!window.confirm('Weet u zeker dat u deze cohort wilt verwijderen?')) return;
    try {
      await deleteCohort(cohortId);
    } catch (error) {
      window.alert(error.message || 'Cohort verwijderen mislukt');
    }
  };

  return (
    <div className="cohorts-page">
      <div className="page-header">
        <div>
          <h1>{t('admin.cohorts')}</h1>
          <p className="page-subtitle">Groepeer studenten per instroom en koppel cohorts aan studies en modules.</p>
        </div>
        <button className="btn-primary" onClick={openCreateModal}>Cohort aanmaken</button>
      </div>

      <div className="users-toolbar">
        <input
          type="search"
          className="users-search"
          placeholder="Zoek cohort op naam, code of locatie"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="cohort-grid">
        {cohorts.map((cohort) => (
          <article key={cohort.id} className="cohort-card">
            <div className="cohort-card-header">
              <div>
                <h2>{cohort.name}</h2>
                <p>{cohort.code || 'Geen code'}</p>
              </div>
              <span className={`status-badge ${cohort.isActive ? 'active' : 'cancelled'}`}>
                {cohort.isActive ? 'Actief' : 'Inactief'}
              </span>
            </div>
            <p className="cohort-description">{cohort.description || 'Geen omschrijving opgegeven.'}</p>
            <div className="cohort-meta">
              <span>{translateLocation(t, String(cohort.location || '').toLowerCase())}</span>
              <span>{cohort.studentCount || 0} studenten</span>
              <span>{(cohort.studies || []).length} studies</span>
              <span>{(cohort.modules || []).length} modules</span>
            </div>
            <div className="cohort-chip-list">
              {(cohort.studies || []).slice(0, 3).map((study) => (
                <span key={`study-${study.id}`} className="registration-course-chip">{study.name}</span>
              ))}
              {(cohort.modules || []).slice(0, 3).map((moduleDoc) => (
                <span key={`module-${moduleDoc.id}`} className="registration-course-chip muted">{moduleDoc.code}</span>
              ))}
            </div>
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => openEditModal(cohort)}>{t('common.edit')}</button>
              {user?.role === 'admin' && (
                <button type="button" className="btn-delete" onClick={() => handleDelete(cohort.id)}>{t('common.delete')}</button>
              )}
            </div>
          </article>
        ))}
        {cohorts.length === 0 && <p className="no-data">{t('common.noData')}</p>}
      </div>

      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title={editingCohort ? 'Cohort bewerken' : 'Cohort aanmaken'}>
        <form onSubmit={handleSubmit} className="cohort-form">
          <div className="form-row">
            <div className="form-group">
              <label>Naam *</label>
              <input type="text" value={cohortForm.name} onChange={(e) => setCohortForm((prev) => ({ ...prev, name: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label>Code</label>
              <input type="text" value={cohortForm.code} onChange={(e) => setCohortForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>{t('location.title')}</label>
              <select
                value={cohortForm.location}
                onChange={(e) => setCohortForm((prev) => ({ ...prev, location: e.target.value }))}
                disabled={user?.role === 'manager'}
              >
                {(data.locations || []).map((loc) => (
                  <option key={loc.id} value={loc.id}>{translateLocation(t, loc.id)}</option>
                ))}
              </select>
            </div>
            <div className="form-group checkbox-field">
              <label>
                <input type="checkbox" checked={cohortForm.isActive} onChange={(e) => setCohortForm((prev) => ({ ...prev, isActive: e.target.checked }))} />
                Actief
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Omschrijving</label>
            <textarea rows={3} value={cohortForm.description} onChange={(e) => setCohortForm((prev) => ({ ...prev, description: e.target.value }))} />
          </div>

          <div className="cohort-link-grid">
            <div className="form-group">
              <label>Student toevoegen (zonder cohort)</label>
              <div className="link-action-row">
                <select value={selectedStudentToAdd} onChange={(e) => setSelectedStudentToAdd(e.target.value)}>
                  <option value="">Selecteer student...</option>
                  {studentsWithoutCohort.map((student) => (
                    <option key={student.id} value={student.id}>{student.name} ({student.email})</option>
                  ))}
                </select>
                <button type="button" className="btn-secondary" onClick={addStudentToCohort} disabled={!selectedStudentToAdd}>Toevoegen</button>
              </div>
            </div>

            <div className="form-group">
              <label>Student verwijderen (huidige cohort)</label>
              <div className="link-action-row">
                <select value={selectedStudentToRemove} onChange={(e) => setSelectedStudentToRemove(e.target.value)}>
                  <option value="">Selecteer student...</option>
                  {studentsInCohort.map((student) => (
                    <option key={student.id} value={student.id}>{student.name} ({student.email})</option>
                  ))}
                </select>
                <button type="button" className="btn-secondary" onClick={removeStudentFromCohort} disabled={!selectedStudentToRemove}>Verwijderen</button>
              </div>
            </div>
          </div>

          <div className="cohort-link-grid">
            <div className="form-group">
              <label>Studie koppelen</label>
              <div className="link-action-row">
                <select value={selectedStudyToAdd} onChange={(e) => setSelectedStudyToAdd(e.target.value)}>
                  <option value="">Selecteer studie...</option>
                  {studiesAvailableToAdd.map((study) => (
                    <option key={study.id} value={study.id}>{study.name}</option>
                  ))}
                </select>
                <button type="button" className="btn-secondary" onClick={addStudyToCohort} disabled={!selectedStudyToAdd}>Koppelen</button>
              </div>
            </div>

            <div className="form-group">
              <label>Studie ontkoppelen</label>
              <div className="link-action-row">
                <select value={selectedStudyToRemove} onChange={(e) => setSelectedStudyToRemove(e.target.value)}>
                  <option value="">Selecteer studie...</option>
                  {studiesInCohort.map((study) => (
                    <option key={study.id} value={study.id}>{study.name}</option>
                  ))}
                </select>
                <button type="button" className="btn-secondary" onClick={removeStudyFromCohort} disabled={!selectedStudyToRemove}>Ontkoppelen</button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Modules per studiejaar</label>
            <div className="module-group-list">
              {moduleGroups.map((group) => (
                <div key={group.key} className="module-group-card">
                  <div className="module-group-header">
                    <strong>{group.studyName}</strong>
                    <span>{group.yearLabel}</span>
                  </div>
                  <div className="module-chip-grid">
                    {group.modules.map((moduleDoc) => {
                      const selected = cohortForm.moduleIds.includes(moduleDoc.id);
                      return (
                        <button
                          key={moduleDoc.id}
                          type="button"
                          className={`module-chip-button ${selected ? 'selected' : ''}`}
                          onClick={() => toggleModuleSelection(moduleDoc.id)}
                        >
                          <span>{moduleDoc.code || 'MODULE'}</span>
                          <small>{moduleDoc.name}</small>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {moduleGroups.length === 0 && (
                <p className="no-data">Koppel eerst een studie om modules per studiejaar te tonen.</p>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">{t('common.save')}</button>
            <button type="button" className="btn-secondary" onClick={() => setShowCreateModal(false)}>{t('common.cancel')}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Cohorts;
